// src/components/fix-actions/template-gallery-modal.tsx
import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { X, Sparkles } from "lucide-react";
import type { ContentTemplate } from "../../services/fix-actions/fix-actions-service";
import { TEMPLATE_ICON_MAP, DEFAULT_TEMPLATE_ICON } from "./template-icons";

interface TemplateGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;

  selectedCategory: string;
  onCategoryChange: (category: string) => void;

  selectedTemplate: string | null;
  onTemplateSelect: (templateId: string | null) => void;

  contentTemplates: ContentTemplate[];

  /** Optional human-readable overrides for category labels */
  categoryLabels?: Record<string, string>;
}

function toTitleCase(s: string) {
  return s
    .replace(/[-_]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(" ");
}

export function TemplateGalleryModal({
  isOpen,
  onClose,
  selectedCategory,
  onCategoryChange,
  selectedTemplate,
  onTemplateSelect,
  contentTemplates,
  categoryLabels,
}: TemplateGalleryModalProps) {
  const [step, setStep] = useState<"gallery" | "configure">("gallery");
  const [formData, setFormData] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    for (const t of contentTemplates) {
      if (t.category) set.add(t.category);
    }
    return Array.from(set).sort();
  }, [contentTemplates]);

  const getCategoryLabel = (cat: string) =>
    categoryLabels?.[cat] || toTitleCase(cat);

  const filteredTemplates =
    selectedCategory === "all"
      ? contentTemplates
      : contentTemplates.filter((t) => t.category === selectedCategory);

  const currentTemplate = contentTemplates.find(
    (t) => t.id === selectedTemplate
  );

  const handleTemplateClick = (templateId: string) => {
    onTemplateSelect(templateId);
    setStep("configure");
  };

  const handleBack = () => {
    setStep("gallery");
    onTemplateSelect(null);
    setFormData({});
  };

  const handleGenerate = () => {
    console.log("Generating with data:", formData);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-hidden rounded-lg flex flex-col"
        style={{ backgroundColor: "var(--background)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div
          className="px-4 md:px-8 py-4 md:py-6 border-b flex items-start justify-between gap-3"
          style={{ borderColor: "var(--border)" }}
        >
          <div className="flex-1 min-w-0">
            <h2
              style={{
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-weight-semibold)",
                marginBottom: "4px",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {step === "gallery"
                ? "Choose a Content Template"
                : `Configure: ${currentTemplate?.name}`}
            </h2>
            <p
              style={{
                color: "var(--muted-foreground)",
                fontSize: "var(--text-sm)",
              }}
            >
              {step === "gallery"
                ? "Select a template to generate AI-optimized content"
                : "Provide inputs to customize your generated content"}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="shrink-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {step === "gallery" ? (
          <>
            {/* Category filters — horizontally scroll if too many for viewport */}
            <div
              className="px-4 md:px-8 py-3 md:py-4 border-b"
              style={{
                borderColor: "var(--border)",
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <style>{`
                .template-cat-scroll::-webkit-scrollbar { display: none; }
              `}</style>
              <div className="template-cat-scroll flex items-center gap-2 w-max min-w-full">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCategoryChange("all")}
                  className="shrink-0 whitespace-nowrap"
                  style={{
                    backgroundColor:
                      selectedCategory === "all" ? "var(--muted)" : "transparent",
                    fontWeight: "var(--font-weight-medium)",
                  }}
                >
                  All Templates
                </Button>

                {availableCategories.map((cat) => (
                  <Button
                    key={cat}
                    variant="ghost"
                    size="sm"
                    onClick={() => onCategoryChange(cat)}
                    className="shrink-0 whitespace-nowrap"
                    style={{
                      backgroundColor:
                        selectedCategory === cat ? "var(--muted)" : "transparent",
                      fontWeight: "var(--font-weight-medium)",
                    }}
                  >
                    {getCategoryLabel(cat)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Template grid */}
            <div className="px-4 md:px-8 py-4 md:py-6 overflow-y-auto flex-1">
              {filteredTemplates.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <p
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    No templates available for this category.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  {filteredTemplates.map((template) => {
                    const Icon =
                      TEMPLATE_ICON_MAP[template.iconKey] ||
                      DEFAULT_TEMPLATE_ICON;

                    return (
                      <button
                        key={template.id}
                        onClick={() => handleTemplateClick(template.id)}
                        className="p-4 md:p-6 border rounded-lg text-left transition-all hover:border-foreground/20"
                        style={{
                          borderColor: "var(--border)",
                          backgroundColor: "var(--background)",
                        }}
                      >
                        <div className="flex items-start gap-3 md:gap-4">
                          <div
                            className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: "var(--muted)" }}
                          >
                            <Icon
                              className="w-5 h-5"
                              style={{ color: "var(--foreground)" }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3
                              style={{
                                fontSize: "var(--text-base)",
                                fontWeight: "var(--font-weight-semibold)",
                                marginBottom: "4px",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {template.name}
                            </h3>
                            <p
                              style={{
                                color: "var(--muted-foreground)",
                                fontSize: "var(--text-sm)",
                                lineHeight: "1.5",
                              }}
                            >
                              {template.description}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Configure step */}
            <div className="px-4 md:px-8 py-4 md:py-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {currentTemplate?.inputs.map((input) => (
                  <div key={input}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-weight-medium)",
                        marginBottom: "8px",
                      }}
                    >
                      {input}
                    </label>
                    <input
                      type="text"
                      value={formData[input] || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, [input]: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-md"
                      style={{
                        backgroundColor: "var(--background)",
                        borderColor: "var(--border)",
                        fontSize: "var(--text-sm)",
                      }}
                      placeholder={`Enter ${input.toLowerCase()}`}
                    />
                  </div>
                ))}

                <div
                  className="p-4 rounded-md border"
                  style={{
                    borderColor: "var(--border)",
                    backgroundColor: "var(--muted)",
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles
                      className="w-4 h-4 shrink-0"
                      style={{ color: "var(--spectral-violet)" }}
                    />
                    <h4
                      style={{
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-weight-semibold)",
                        color: "var(--spectral-violet)",
                      }}
                    >
                      AI Enhancement Options
                    </h4>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        defaultChecked
                        style={{ accentColor: "var(--spectral-violet)" }}
                      />
                      <span style={{ fontSize: "var(--text-sm)" }}>
                        Optimize for assistant comprehension
                      </span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        defaultChecked
                        style={{ accentColor: "var(--spectral-violet)" }}
                      />
                      <span style={{ fontSize: "var(--text-sm)" }}>
                        Include SEO best practices
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer — buttons stack on mobile */}
            <div
              className="px-4 md:px-8 py-3 md:py-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
              style={{ borderColor: "var(--border)" }}
            >
              <Button
                variant="ghost"
                onClick={handleBack}
                className="w-full sm:w-auto justify-center"
              >
                Back to Templates
              </Button>
              <div className="flex items-center gap-2 flex-col sm:flex-row">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="w-full sm:w-auto"
                  style={{
                    backgroundColor: "var(--background)",
                    borderColor: "var(--border)",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerate}
                  className="w-full sm:w-auto"
                  style={{
                    backgroundColor: "var(--spectral-violet)",
                    color: "var(--background)",
                    fontWeight: "var(--font-weight-semibold)",
                  }}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Content
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}